import React, { useState, useEffect, useCallback } from 'react';
import { inventoryApi } from '../api/client';
import { Badge, Modal, Spin, Empty, useFlash, F, parseErr } from '../components/UI';
import { Trash, Edit, Package } from '../components/Icons';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['Inverter','Battery','Trolley','Solar Panel','UPS','Solar Inverter','Accessory'];
const CATEGORY_BRANDS = {
  'Inverter':    ['Sakthi'],
  'Battery':     ['Exide','Triotec','Luminous','Power Zone','SF'],
  'Solar Panel': ['Waaree','Adani'],
};

const STATUS_STYLE = {
  in_stock: { color:'var(--green)', bg:'rgba(22,163,74,0.08)' },
  sold:     { color:'var(--text2)', bg:'rgba(100,116,139,0.1)' },
};

export default function Inventory() {
  const { canDelete, canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const { show, Flash } = useFlash();

  // Add product modal
  const [showAdd, setAdd] = useState(false);
  const [form, setForm] = useState({ sku:'', name:'', category:'Inverter', brand:'', mrp:'', cost_price:'', warranty_months:'', barcode:'', low_stock_threshold:'5', description:'' });
  const [serialRows, setSerialRows] = useState(['']);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setSerialRow = (i, val) => setSerialRows(r => r.map((v, j) => j === i ? val : v));
  const addSerialRow = () => setSerialRows(r => [...r, '']);
  const removeSerialRow = i => setSerialRows(r => r.filter((_, j) => j !== i));

  // Edit product modal (MRP / barcode)
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ mrp:'', barcode:'' });
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr] = useState('');
  const setEdit = k => e => setEditForm(f => ({ ...f, [k]: e.target.value }));

  // Serials modal
  const [serialProd, setSerialProd] = useState(null);
  const [newSerials, setNewSerials] = useState('');
  const [serialSaving, setSerialSaving] = useState(false);
  const [serialErr, setSerialErr] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await inventoryApi.list(); setRows(data); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = p => {
    setEditRow(p);
    setEditForm({ mrp: String(p.mrp), barcode: p.barcode || '', warranty_months: p.warranty_months > 0 ? String(p.warranty_months) : '' });
    setEditErr('');
  };

  const saveEdit = async () => {
    if (!editForm.mrp || isNaN(parseFloat(editForm.mrp))) { setEditErr('Valid MRP is required.'); return; }
    setEditSaving(true); setEditErr('');
    try {
      const { data } = await inventoryApi.update(editRow.id, {
        mrp: parseFloat(editForm.mrp),
        barcode: editForm.barcode || null,
        warranty_months: editForm.warranty_months ? parseInt(editForm.warranty_months) : 0,
      });
      setRows(r => r.map(x => x.id === data.id ? data : x));
      show(`${data.name} updated!`);
      setEditRow(null);
    } catch(e) { setEditErr(parseErr(e, 'Failed to update.')); }
    finally { setEditSaving(false); }
  };

  const save = async () => {
    if (!form.sku || !form.name || !form.mrp) { setErr('Model No., name and MRP required.'); return; }
    const validSerials = serialRows.map(s => s.trim()).filter(Boolean);
    const dupes = validSerials.filter((s, i) => validSerials.findIndex(x => x.toLowerCase() === s.toLowerCase()) !== i);
    if (dupes.length > 0) { setErr(`Duplicate serial numbers: ${dupes.join(', ')}`); return; }
    setSaving(true); setErr('');
    try {
      const { data: prod } = await inventoryApi.create({
        ...form,
        mrp: parseFloat(form.mrp),
        cost_price: parseFloat(form.cost_price) || 0,
        warranty_months: form.warranty_months ? parseInt(form.warranty_months) : 0,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 5,
        barcode: form.barcode || null,
        serials: validSerials,
      });
      setRows(r => [prod, ...r]);
      show(`${prod.name} added${prod.serials?.length ? ` — ${prod.serials.length} unit(s)` : ''}!`);
      setAdd(false);
      setForm({ sku:'', name:'', category:'Inverter', brand:'', mrp:'', cost_price:'', warranty_months:'', barcode:'', low_stock_threshold:'5', description:'' });
      setSerialRows(['']);
    } catch(e) { setErr(parseErr(e, 'Failed to add product.')); }
    finally { setSaving(false); }
  };

  const softDel = async id => {
    try { await inventoryApi.delete(id); setRows(r => r.filter(x => x.id !== id)); show('Removed.'); }
    catch { show('Failed.', 'error'); }
  };

  const openSerials = p => {
    setSerialProd(p);
    setNewSerials('');
    setSerialErr('');
  };

  const addSerials = async () => {
    const list = newSerials.split('\n').map(s => s.trim()).filter(Boolean);
    if (!list.length) { setSerialErr('Enter at least one serial number.'); return; }
    const dupeInInput = list.filter((s, i) => list.findIndex(x => x.toLowerCase() === s.toLowerCase()) !== i);
    if (dupeInInput.length > 0) { setSerialErr(`Duplicate in your input: ${dupeInInput[0]}`); return; }
    const existing = (serialProd.serials || []).map(s => s.serial_number.toLowerCase());
    const conflicts = list.filter(s => existing.includes(s.toLowerCase()));
    if (conflicts.length > 0) { setSerialErr(`Already exists: ${conflicts.join(', ')}`); return; }
    setSerialSaving(true); setSerialErr('');
    try {
      const { data: added } = await inventoryApi.addSerials(serialProd.id, list);
      const pid = serialProd.id;
      const patch = p => ({ ...p, serials: [...p.serials, ...added], stock_qty: p.stock_qty + added.length });
      setRows(r => r.map(p => p.id === pid ? patch(p) : p));
      setSerialProd(prev => patch(prev));
      setNewSerials('');
      show(`${added.length} serial(s) added.`);
    } catch(e) { setSerialErr(parseErr(e, 'Failed to add serials.')); }
    finally { setSerialSaving(false); }
  };

  const removeSerial = async (pid, sid, sn) => {
    if (!window.confirm(`Remove serial ${sn}? Stock will decrease by 1.`)) return;
    try {
      await inventoryApi.deleteSerial(pid, sid);
      const patch = p => ({ ...p, serials: p.serials.filter(s => s.id !== sid), stock_qty: Math.max(0, p.stock_qty - 1) });
      setRows(r => r.map(p => p.id === pid ? patch(p) : p));
      setSerialProd(prev => prev?.id === pid ? patch(prev) : prev);
      show('Serial removed.');
    } catch { show('Failed.', 'error'); }
  };

  if (busy) return <Spin/>;

  return (
    <div>
      <Flash/>
      <div className="page-hd">
        <h2>Inventory</h2>
        {canWrite && <button className="btn btn-primary" onClick={() => setAdd(true)}>+ Add Product</button>}
      </div>

      <div className="card card-0">
        {rows.length === 0 ? <Empty msg="No products in inventory"/> : (
          <div className="tbl-wrap"><table>
            <thead><tr>
              <th>Model No.</th><th>Product</th><th>Category</th><th>Brand</th>
              <th>Barcode</th><th>Serials</th><th>Stock</th><th>MRP</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id}>
                  <td style={{ fontFamily:'monospace', fontSize:11 }}>{p.sku}</td>
                  <td>
                    <div style={{ fontWeight:500 }}>
                      {p.name}
                      {p.warranty_months > 0 && (
                        <span style={{ marginLeft:6, fontSize:11, color:'var(--navy)', fontWeight:400 }}>
                          ({p.warranty_months} months Warranty)
                        </span>
                      )}
                    </div>
                    {p.created_by_name && (
                      <div style={{ fontSize:10, color:'var(--text2)', marginTop:2 }}>
                        Added by {p.created_by_name} · {new Date(p.created_at).toLocaleDateString('en-IN', { dateStyle:'medium' })}
                      </div>
                    )}
                  </td>
                  <td><Badge label={p.category}/></td>
                  <td>{p.brand || '—'}</td>
                  <td style={{ fontFamily:'monospace', fontSize:11 }}>{p.barcode || '—'}</td>
                  <td>
                    <button className="btn btn-sm" style={{ fontSize:11 }} onClick={() => openSerials(p)}>
                      <Package s={11}/> {p.serials.length} unit{p.serials.length !== 1 ? 's' : ''}
                    </button>
                  </td>
                  <td>
                    <span className={`badge ${p.stock_qty <= p.low_stock_threshold ? (p.stock_qty <= 3 ? 'b-err' : 'b-warn') : 'b-ok'}`}>
                      {p.stock_qty}
                    </span>
                  </td>
                  <td style={{ fontWeight:600 }}>₹{Number(p.mrp).toLocaleString('en-IN')}</td>
                  <td style={{ display:'flex', gap:4 }}>
                    {canWrite && <button className="btn btn-sm" onClick={() => openEdit(p)} title="Edit"><Edit s={13}/></button>}
                    {canDelete && <button className="btn btn-sm btn-danger" onClick={() => softDel(p.id)} title="Remove"><Trash s={13}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Add Product Modal */}
      <Modal open={showAdd} onClose={() => { setAdd(false); setSerialRows(['']); setErr(''); }} title="Add Product to Inventory"
        footer={<><button className="btn" onClick={() => setAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Adding…' : 'Add Product'}</button></>}
      >
        {err && <div className="notice n-err" style={{ marginBottom:10 }}>{err}</div>}
        <div className="fg-2">
          <F label="Product Name" req full><input value={form.name} onChange={set('name')} placeholder="e.g. Luminous 3.5kVA Inverter"/></F>
          <F label="Model No." req><input value={form.sku} onChange={set('sku')} placeholder="e.g. LUM-35K"/></F>
          <F label="Category" req>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value, brand: '' }))}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </F>
          <F label="Brand">
            {CATEGORY_BRANDS[form.category]
              ? <select value={form.brand} onChange={set('brand')}>
                  <option value="">— Select brand —</option>
                  {CATEGORY_BRANDS[form.category].map(b => <option key={b}>{b}</option>)}
                </select>
              : <input value={form.brand} onChange={set('brand')} placeholder="Brand"/>
            }
          </F>
          <F label="MRP (₹)" req><input type="number" value={form.mrp} onChange={set('mrp')}/></F>
          <F label="Cost Price (₹)"><input type="number" value={form.cost_price} onChange={set('cost_price')}/></F>
          <F label="Warranty (months)">
            <input type="number" min="0" value={form.warranty_months} onChange={set('warranty_months')} placeholder="e.g. 36"/>
          </F>
          <F label="Low Stock Alert"><input type="number" value={form.low_stock_threshold} onChange={set('low_stock_threshold')}/></F>
          <F label="Barcode"><input value={form.barcode} onChange={set('barcode')} placeholder="e.g. 8901234567890"/></F>
          <F label="Description / Specs" full><textarea value={form.description} onChange={set('description')} placeholder="Specs, notes…"/></F>
          {form.warranty_months > 0 && (
            <div style={{ gridColumn:'1/-1', fontSize:12, color:'var(--navy)', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'7px 12px' }}>
              Preview: <strong>{form.name || 'Product name'}</strong>{form.warranty_months ? ` (${form.warranty_months} months Warranty)` : ''}
            </div>
          )}

          {/* Serial Numbers */}
          <div style={{ gridColumn:'1/-1' }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>
              Serial Numbers <span style={{ fontWeight:400 }}>(each serial = 1 unit in stock)</span>
            </div>
            {serialRows.map((sn, i) => (
              <div key={i} style={{ display:'flex', gap:6, marginBottom:6, alignItems:'center' }}>
                <input
                  value={sn}
                  onChange={e => setSerialRow(i, e.target.value)}
                  placeholder={`Serial No. ${i + 1}`}
                  style={{ flex:1 }}
                />
                {serialRows.length > 1 && (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeSerialRow(i)}
                    style={{ flexShrink:0, padding:'0 8px', lineHeight:'28px' }}>✕</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-sm" onClick={addSerialRow} style={{ marginTop:2 }}>
              + Add Row
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Product Modal */}
      <Modal open={!!editRow} onClose={() => setEditRow(null)} title={`Edit: ${editRow?.name || ''}`}
        footer={<><button className="btn" onClick={() => setEditRow(null)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button></>}
      >
        {editErr && <div className="notice n-err" style={{ marginBottom:10 }}>{editErr}</div>}
        <div className="fg-2">
          <F label="MRP (₹)" req><input type="number" value={editForm.mrp} onChange={setEdit('mrp')}/></F>
          <F label="Barcode"><input value={editForm.barcode} onChange={setEdit('barcode')} placeholder="e.g. 8901234567890"/></F>
        </div>
      </Modal>

      {/* Manage Serials Modal */}
      <Modal open={!!serialProd} onClose={() => setSerialProd(null)}
        title={`Serial Numbers — ${serialProd?.name || ''}`}
        footer={<button className="btn" onClick={() => setSerialProd(null)}>Close</button>}
      >
        {serialProd && (
          <div>
            {/* Existing serials list */}
            {serialProd.serials.length === 0
              ? <div style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>No serial numbers added yet.</div>
              : <div style={{ marginBottom:16, maxHeight:220, overflowY:'auto', border:'1px solid var(--border)', borderRadius:6 }}>
                  <table style={{ width:'100%', fontSize:12 }}>
                    <thead><tr style={{ background:'var(--surface)' }}>
                      <th style={{ padding:'6px 10px', textAlign:'left' }}>Serial Number</th>
                      <th style={{ padding:'6px 10px', textAlign:'left' }}>Status</th>
                      {canWrite && <th style={{ padding:'6px 4px' }}></th>}
                    </tr></thead>
                    <tbody>
                      {serialProd.serials.map(s => {
                        const st = STATUS_STYLE[s.status] || STATUS_STYLE.in_stock;
                        return (
                          <tr key={s.id} style={{ borderTop:'1px solid var(--border)' }}>
                            <td style={{ padding:'6px 10px', fontFamily:'monospace' }}>{s.serial_number}</td>
                            <td style={{ padding:'6px 10px' }}>
                              <span style={{ fontSize:10, fontWeight:600, color:st.color, background:st.bg, borderRadius:4, padding:'2px 6px' }}>
                                {s.status === 'in_stock' ? 'In Stock' : 'Sold'}
                              </span>
                            </td>
                            {canWrite && (
                              <td style={{ padding:'4px 6px' }}>
                                {canDelete && (
                                  <button className="btn btn-sm btn-danger" style={{ padding:'2px 6px', fontSize:11 }}
                                    onClick={() => removeSerial(serialProd.id, s.id, s.serial_number)}>
                                    <Trash s={11}/>
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            }

            {/* Add new serials */}
            {canWrite && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:'var(--text2)' }}>
                  Add Serial Numbers <span style={{ fontWeight:400 }}>(one per line)</span>
                </div>
                {serialErr && <div className="notice n-err" style={{ marginBottom:8, fontSize:12 }}>{serialErr}</div>}
                <textarea
                  value={newSerials}
                  onChange={e => setNewSerials(e.target.value)}
                  placeholder={"SN-001\nSN-002\nSN-003"}
                  style={{ width:'100%', minHeight:80, fontFamily:'monospace', fontSize:12, resize:'vertical', boxSizing:'border-box' }}
                />
                <div style={{ marginTop:8, display:'flex', justifyContent:'flex-end' }}>
                  <button className="btn btn-primary" onClick={addSerials} disabled={serialSaving}>
                    {serialSaving ? 'Adding…' : '+ Add Serials'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
